import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { SectionHeader, Spinner, EmptyState, ConfirmModal, Tabs } from '../../components/ui';
import { toast } from '../../store';
import api from '../../api/client';
import { cn } from '../../lib/utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const GROUPS = ['NSE_BSE', 'MCX'];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

// MCX symbol: update when front-month contract rolls (e.g. CRUDEOIL25MAYFUT → CRUDEOIL25JUNFUT)
const STATUS_SYMBOLS = {
  NSE_BSE: 'NSE:NIFTY50-INDEX',
  MCX:     'MCX:CRUDEOIL25MAYFUT',
};

const TAB_DEFS = [
  { id: 'sessions',  label: 'Sessions'  },
  { id: 'holidays',  label: 'Holidays'  },
  { id: 'overrides', label: 'Overrides' },
];

// ─── Inline query hooks ───────────────────────────────────────────────────────

function useMarketStatus(symbol) {
  return useQuery({
    queryKey: ['marketStatus', symbol],
    queryFn: () => api.get('/admin/market/status', { params: { symbol } }),
    refetchInterval: 15_000,
    retry: false,
  });
}

function useSessions(group) {
  return useQuery({
    queryKey: ['marketSessions', group],
    queryFn: () => api.get('/admin/market/sessions', { params: { group } }),
    staleTime: 30_000,
  });
}

function useHolidays(group, year) {
  return useQuery({
    queryKey: ['marketHolidays', group, year],
    queryFn: () => api.get('/admin/market/holidays', { params: { group, year } }),
    staleTime: 60_000,
  });
}

function useOverrides(group, year) {
  return useQuery({
    queryKey: ['marketOverrides', group, year],
    queryFn: () => api.get('/admin/market/overrides', { params: { group, year } }),
    staleTime: 60_000,
  });
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function BoolBadge({ value }) {
  return (
    <span className={cn('text-2xs font-mono font-semibold', value ? 'text-bull' : 'text-bear')}>
      {value ? '✓' : '✗'}
    </span>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-0.5">
      <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</label>
      {children}
    </div>
  );
}

const INPUT_CLS = 'input-base w-full';
const CHECK_CLS = 'w-4 h-4 accent-amber cursor-pointer';

function CheckField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className={CHECK_CLS} />
      <span className="text-xs font-mono text-txt-secondary">{label}</span>
    </label>
  );
}

// ─── StatusCard ───────────────────────────────────────────────────────────────

function StatusCard({ group }) {
  const symbol = STATUS_SYMBOLS[group];
  const { data, isLoading, isError, refetch, isFetching } = useMarketStatus(symbol);

  return (
    <div className="card p-4 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{group.replace('_', '/')}</div>
          <div className="text-xs font-mono text-txt-muted/60 mt-0.5">{symbol}</div>
        </div>
        <button onClick={refetch} disabled={isFetching}
          className="p-1 rounded text-txt-muted hover:text-amber hover:bg-amber/10 transition-colors">
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {isLoading && <Spinner />}

      {isError && (
        <p className="text-2xs font-mono text-bear">Failed to load status.</p>
      )}

      {data && (
        <>
          <div className={cn(
            'inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold',
            data.is_open ? 'bg-bull/10 text-bull' : 'bg-bear/10 text-bear'
          )}>
            {data.is_open ? 'OPEN' : 'CLOSED'}
          </div>

          <div className="space-y-1">
            {data.session && (
              <div className="flex items-center gap-1.5">
                <span className="text-2xs font-mono text-txt-muted w-20">Session</span>
                <span className="text-2xs font-mono text-txt-secondary">{data.session}</span>
              </div>
            )}
            {[
              ['Candles',  data.candle_allowed],
              ['Strategy', data.strategy_allowed],
              ['Orders',   data.order_allowed],
            ].map(([lbl, val]) => (
              <div key={lbl} className="flex items-center gap-1.5">
                <span className="text-2xs font-mono text-txt-muted w-20">{lbl}</span>
                <BoolBadge value={val} />
              </div>
            ))}
            {data.reason && (
              <div className="text-2xs font-mono text-txt-muted/60 italic pt-0.5">{data.reason}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── SessionModal ─────────────────────────────────────────────────────────────

function SessionModal({ session, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...session });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.start_time || !form.end_time) return 'Start and end time are required.';
    if (form.start_time >= form.end_time) return 'Start time must be before end time.';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.put(`/admin/market/sessions/${form.id}`, form);
      qc.invalidateQueries({ queryKey: ['marketSessions'] });
      toast.success('Session updated');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to update session.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="card p-6 w-full max-w-sm animate-fade-in shadow-panel space-y-4">
        <h3 className="font-display font-semibold text-txt-primary">
          Edit Session — {form.session_name || form.name}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Start Time">
            <input type="time" value={form.start_time || ''} onChange={(e) => set('start_time', e.target.value)}
              className={INPUT_CLS} />
          </FieldRow>
          <FieldRow label="End Time">
            <input type="time" value={form.end_time || ''} onChange={(e) => set('end_time', e.target.value)}
              className={INPUT_CLS} />
          </FieldRow>
        </div>

        <div className="space-y-2 pt-1">
          <CheckField label="Candle Allowed"   checked={form.candle_allowed}   onChange={(v) => set('candle_allowed', v)} />
          <CheckField label="Strategy Allowed" checked={form.strategy_allowed} onChange={(v) => set('strategy_allowed', v)} />
          <CheckField label="Order Allowed"    checked={form.order_allowed}    onChange={(v) => set('order_allowed', v)} />
          <CheckField label="Active"           checked={form.is_active}        onChange={(v) => set('is_active', v)} />
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SessionsTab ──────────────────────────────────────────────────────────────

function SessionsTab({ group }) {
  const { data, isLoading, isError } = useSessions(group);
  const [editing, setEditing] = useState(null);
  const sessions = Array.isArray(data) ? data : (data?.sessions ?? []);

  return (
    <div className="panel overflow-hidden">
      {isLoading && <div className="flex justify-center py-10"><Spinner /></div>}
      {isError   && <p className="text-xs text-bear font-mono px-4 py-4">Failed to load sessions.</p>}
      {!isLoading && !isError && !sessions.length && (
        <div className="p-8"><EmptyState title="No sessions" description="No sessions found for this group." /></div>
      )}
      {!isLoading && !isError && sessions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-bg-panel z-10">
              <tr className="border-b border-bg-border">
                {['Session', 'Start', 'End', 'Candle', 'Strategy', 'Order', 'Active', ''].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="border-b border-bg-border/30 hover:bg-bg-hover transition-colors">
                  <td className="py-2.5 px-3 font-mono text-txt-primary">{s.session_name || s.name}</td>
                  <td className="py-2.5 px-3 font-mono text-txt-secondary tabular-nums">{s.start_time}</td>
                  <td className="py-2.5 px-3 font-mono text-txt-secondary tabular-nums">{s.end_time}</td>
                  <td className="py-2.5 px-3"><BoolBadge value={s.candle_allowed} /></td>
                  <td className="py-2.5 px-3"><BoolBadge value={s.strategy_allowed} /></td>
                  <td className="py-2.5 px-3"><BoolBadge value={s.order_allowed} /></td>
                  <td className="py-2.5 px-3"><BoolBadge value={s.is_active} /></td>
                  <td className="py-2.5 px-3">
                    <button onClick={() => setEditing(s)}
                      className="p-1 rounded text-txt-muted hover:text-amber hover:bg-amber/10 transition-colors">
                      <Pencil size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <SessionModal session={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ─── HolidayModal ─────────────────────────────────────────────────────────────

function HolidayModal({ holiday, group, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!holiday?.id;
  const [form, setForm] = useState({
    holiday_date: '',
    holiday_name: '',
    is_active: true,
    market_group_code: group,
    ...holiday,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.holiday_date) { toast.error('Date is required.'); return; }
    if (!form.holiday_name) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/market/holidays/${form.id}`, form);
      else        await api.post('/admin/market/holidays', form);
      qc.invalidateQueries({ queryKey: ['marketHolidays'] });
      toast.success(isEdit ? 'Holiday updated' : 'Holiday created');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to save holiday.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="card p-6 w-full max-w-sm animate-fade-in shadow-panel space-y-4">
        <h3 className="font-display font-semibold text-txt-primary">
          {isEdit ? 'Edit Holiday' : 'Add Holiday'}
        </h3>
        <FieldRow label="Date">
          <input type="date" value={form.holiday_date || ''} onChange={(e) => set('holiday_date', e.target.value)}
            className={INPUT_CLS} />
        </FieldRow>
        <FieldRow label="Holiday Name">
          <input type="text" value={form.holiday_name || ''} onChange={(e) => set('holiday_name', e.target.value)}
            placeholder="e.g. Republic Day" className={INPUT_CLS} />
        </FieldRow>
        <CheckField label="Active" checked={form.is_active} onChange={(v) => set('is_active', v)} />
        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Add Holiday'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HolidaysTab ──────────────────────────────────────────────────────────────

function HolidaysTab({ group, year }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useHolidays(group, year);
  const [modal, setModal]     = useState(null); // null | {} | holiday object
  const [delTarget, setDel]   = useState(null);
  const [deleting, setDeleting] = useState(false);
  const holidays = Array.isArray(data) ? data : (data?.holidays ?? []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/market/holidays/${delTarget.id}`);
      qc.invalidateQueries({ queryKey: ['marketHolidays'] });
      toast.success('Holiday deleted');
      setDel(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to delete holiday.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setModal({})} className="btn-primary flex items-center gap-1.5 text-xs">
          <Plus size={13} /> Add Holiday
        </button>
      </div>

      <div className="panel overflow-hidden">
        {isLoading && <div className="flex justify-center py-10"><Spinner /></div>}
        {isError   && <p className="text-xs text-bear font-mono px-4 py-4">Failed to load holidays.</p>}
        {!isLoading && !isError && !holidays.length && (
          <div className="p-8"><EmptyState title="No holidays" description={`No holidays for ${group} in ${year}.`} /></div>
        )}
        {!isLoading && !isError && holidays.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-bg-border">
                  {['Date', 'Name', 'Active', ''].map((h) => (
                    <th key={h} className="text-left py-2.5 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {holidays.map((h) => (
                  <tr key={h.id} className="border-b border-bg-border/30 hover:bg-bg-hover transition-colors">
                    <td className="py-2.5 px-3 font-mono text-txt-primary tabular-nums">{h.holiday_date}</td>
                    <td className="py-2.5 px-3 text-txt-secondary">{h.holiday_name}</td>
                    <td className="py-2.5 px-3"><BoolBadge value={h.is_active} /></td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setModal(h)}
                          className="p-1 rounded text-txt-muted hover:text-amber hover:bg-amber/10 transition-colors">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => setDel(h)}
                          className="p-1 rounded text-txt-muted hover:text-bear hover:bg-bear/10 transition-colors">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal !== null && (
        <HolidayModal
          holiday={modal?.id ? modal : undefined}
          group={group}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmModal
        open={!!delTarget}
        title="Delete Holiday"
        message={`Delete "${delTarget?.holiday_name}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onConfirm={handleDelete}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}

// ─── OverrideModal ────────────────────────────────────────────────────────────

function OverrideModal({ override, group, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!override?.id;
  const [form, setForm] = useState({
    override_date: '',
    session_name: '',
    start_time: '',
    end_time: '',
    candle_allowed: true,
    strategy_allowed: true,
    order_allowed: true,
    note: '',
    is_active: true,
    market_group_code: group,
    ...override,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.override_date) return 'Date is required.';
    if (!form.session_name)  return 'Session name is required.';
    if (form.start_time && form.end_time && form.start_time >= form.end_time)
      return 'Start time must be before end time.';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/market/overrides/${form.id}`, form);
      else        await api.post('/admin/market/overrides', form);
      qc.invalidateQueries({ queryKey: ['marketOverrides'] });
      toast.success(isEdit ? 'Override updated' : 'Override created');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to save override.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="card p-6 w-full max-w-md animate-fade-in shadow-panel space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="font-display font-semibold text-txt-primary">
          {isEdit ? 'Edit Override' : 'Add Override'}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Date">
            <input type="date" value={form.override_date || ''} onChange={(e) => set('override_date', e.target.value)}
              className={INPUT_CLS} />
          </FieldRow>
          <FieldRow label="Session Name">
            <input type="text" value={form.session_name || ''} onChange={(e) => set('session_name', e.target.value)}
              placeholder="e.g. muhurat" className={INPUT_CLS} />
          </FieldRow>
          <FieldRow label="Start Time">
            <input type="time" value={form.start_time || ''} onChange={(e) => set('start_time', e.target.value)}
              className={INPUT_CLS} />
          </FieldRow>
          <FieldRow label="End Time">
            <input type="time" value={form.end_time || ''} onChange={(e) => set('end_time', e.target.value)}
              className={INPUT_CLS} />
          </FieldRow>
        </div>

        <FieldRow label="Note">
          <input type="text" value={form.note || ''} onChange={(e) => set('note', e.target.value)}
            placeholder="Optional note" className={INPUT_CLS} />
        </FieldRow>

        <div className="space-y-2 pt-1">
          <CheckField label="Candle Allowed"   checked={form.candle_allowed}   onChange={(v) => set('candle_allowed', v)} />
          <CheckField label="Strategy Allowed" checked={form.strategy_allowed} onChange={(v) => set('strategy_allowed', v)} />
          <CheckField label="Order Allowed"    checked={form.order_allowed}    onChange={(v) => set('order_allowed', v)} />
          <CheckField label="Active"           checked={form.is_active}        onChange={(v) => set('is_active', v)} />
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Add Override'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── OverridesTab ─────────────────────────────────────────────────────────────

function OverridesTab({ group, year }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useOverrides(group, year);
  const [modal, setModal]     = useState(null);
  const [delTarget, setDel]   = useState(null);
  const [deleting, setDeleting] = useState(false);
  const overrides = Array.isArray(data) ? data : (data?.overrides ?? []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/market/overrides/${delTarget.id}`);
      qc.invalidateQueries({ queryKey: ['marketOverrides'] });
      toast.success('Override deleted');
      setDel(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to delete override.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setModal({})} className="btn-primary flex items-center gap-1.5 text-xs">
          <Plus size={13} /> Add Override
        </button>
      </div>

      <div className="panel overflow-hidden">
        {isLoading && <div className="flex justify-center py-10"><Spinner /></div>}
        {isError   && <p className="text-xs text-bear font-mono px-4 py-4">Failed to load overrides.</p>}
        {!isLoading && !isError && !overrides.length && (
          <div className="p-8">
            <EmptyState title="No overrides" description={`No overrides for ${group} in ${year}.`} />
          </div>
        )}
        {!isLoading && !isError && overrides.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-bg-border">
                  {['Date', 'Session', 'Start', 'End', 'Candle', 'Strategy', 'Order', 'Note', 'Active', ''].map((h) => (
                    <th key={h} className="text-left py-2.5 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overrides.map((o) => (
                  <tr key={o.id} className="border-b border-bg-border/30 hover:bg-bg-hover transition-colors">
                    <td className="py-2.5 px-3 font-mono text-txt-primary tabular-nums whitespace-nowrap">{o.override_date}</td>
                    <td className="py-2.5 px-3 font-mono text-txt-secondary">{o.session_name}</td>
                    <td className="py-2.5 px-3 font-mono tabular-nums text-txt-muted">{o.start_time}</td>
                    <td className="py-2.5 px-3 font-mono tabular-nums text-txt-muted">{o.end_time}</td>
                    <td className="py-2.5 px-3"><BoolBadge value={o.candle_allowed} /></td>
                    <td className="py-2.5 px-3"><BoolBadge value={o.strategy_allowed} /></td>
                    <td className="py-2.5 px-3"><BoolBadge value={o.order_allowed} /></td>
                    <td className="py-2.5 px-3 text-txt-muted max-w-[120px] truncate" title={o.note}>{o.note || '—'}</td>
                    <td className="py-2.5 px-3"><BoolBadge value={o.is_active} /></td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setModal(o)}
                          className="p-1 rounded text-txt-muted hover:text-amber hover:bg-amber/10 transition-colors">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => setDel(o)}
                          className="p-1 rounded text-txt-muted hover:text-bear hover:bg-bear/10 transition-colors">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal !== null && (
        <OverrideModal
          override={modal?.id ? modal : undefined}
          group={group}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmModal
        open={!!delTarget}
        title="Delete Override"
        message={`Delete override for "${delTarget?.override_date} — ${delTarget?.session_name}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onConfirm={handleDelete}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminMarketCalendar() {
  const [group, setGroup] = useState('NSE_BSE');
  const [year, setYear]   = useState(CURRENT_YEAR);
  const [tab, setTab]     = useState('sessions');

  return (
    <div className="max-w-6xl space-y-4">
      <SectionHeader
        title="Market Calendar"
        subtitle="Sessions, holidays, and trading overrides"
      />

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {GROUPS.map((g) => <StatusCard key={g} group={g} />)}
      </div>

      {/* Group + Year selectors */}
      <div className="panel p-3 flex flex-wrap items-end gap-4">
        <div>
          <div className="field-label mb-1">Market Group</div>
          <div className="flex gap-1">
            {GROUPS.map((g) => (
              <button
                key={g}
                onClick={() => setGroup(g)}
                className={cn(
                  'px-3 py-1 rounded text-xs font-mono border transition-colors',
                  group === g
                    ? 'border-amber/40 bg-amber/10 text-amber'
                    : 'border-bg-border text-txt-muted hover:border-amber/30 hover:text-txt-primary'
                )}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="field-label mb-1">Year</div>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="bg-bg-card border border-bg-border rounded px-2 py-1 text-xs font-mono text-txt-primary"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={TAB_DEFS} active={tab} onChange={setTab} />

      {tab === 'sessions'  && <SessionsTab  group={group} />}
      {tab === 'holidays'  && <HolidaysTab  group={group} year={year} />}
      {tab === 'overrides' && <OverridesTab group={group} year={year} />}
    </div>
  );
}
